import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdFlight,
  MdSettings,
  MdInfo,
  MdBusiness,
  MdPerson,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected", 3: "Expired" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626", 3: "#6B7280" };

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 220px 1fr",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={22} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFFAA",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg,#3B82F6 0%,#60A5FA 50%,#93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: 6,
      }}
    >
      <Icon size={22} />
      <strong>{title}</strong>
    </div>
    {children}
  </div>
);

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.aircraftCharterWanted.confirmStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
        Confirm Status
      </div>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={{ width: "100%", height: 36, marginBottom: 16 }}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const AircraftCharterWantedDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    api.aircraftCharterWanted.getById(id).then((r: any) => {
      setData(r?.data?.data ?? r?.data);
    });
  }, [id]);

  if (!data) return null;

  return (
    <Modal open fullScreen onClose={onClose}>
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, marginBottom: 24 }}>
          Aircraft Charter Wanted
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
            gap: 20,
          }}
        >
          <Card icon={MdInfo} title="General">
            <InfoRow icon={MdInfo} label="Headline" value={data.headline} />
            <InfoRow
              icon={MdInfo}
              label="Description"
              value={data.description}
            />
            <InfoRow
              icon={MdInfo}
              label="Confirm Status"
              value={
                <Badge
                  label={(ConfirmStatusText as any)[data.confirmStatus]}
                  color={(ConfirmStatusColor as any)[data.confirmStatus]}
                />
              }
            />
          </Card>

          <Card icon={MdFlight} title="Aircraft Criteria">
            <InfoRow
              icon={MdFlight}
              label="Aircraft Type"
              value={data.anyAircraftType ? "Any" : data.aircraftType?.name}
            />
            <InfoRow icon={MdSettings} label="Capacity" value={data.capacity} />
            <InfoRow
              icon={MdSettings}
              label="Backup Aircraft"
              value={<BoolBadge value={data.backupAircraft} />}
            />
          </Card>

          <Card icon={MdSettings} title="Engine Criteria">
            <InfoRow
              icon={MdSettings}
              label="Engine Type"
              value={data.anyEngineType ? "Any" : data.engineType?.code}
            />
            <InfoRow
              icon={MdSettings}
              label="Min Monthly Block Hour"
              value={data.minimumMonthlyBlockHourGuarantee}
            />
          </Card>

          <Card icon={MdInfo} title="Period & Location">
            <InfoRow
              icon={MdInfo}
              label="Period"
              value={`${new Date(
                data.wantedStartDate
              ).toLocaleDateString()} - ${new Date(
                data.wantedEndDate
              ).toLocaleDateString()}`}
            />
            <InfoRow
              icon={MdInfo}
              label="Airport"
              value={data.location?.airport?.name}
            />
            <InfoRow
              icon={MdInfo}
              label="Country"
              value={data.location?.country?.name}
            />
            <InfoRow
              icon={MdInfo}
              label="City"
              value={data.location?.city?.name}
            />
          </Card>

          <Card icon={MdBusiness} title="Charter Company">
            <InfoRow
              icon={MdBusiness}
              label="Company Name"
              value={data.charterCompanyName}
            />
          </Card>

          <Card icon={MdPerson} title="User">
            <InfoRow
              icon={MdPerson}
              label="Username"
              value={data.user?.username}
            />
            <InfoRow icon={MdInfo} label="Email" value={data.user?.email} />
          </Card>

          <Card icon={MdBusiness} title="Company">
            <InfoRow
              icon={MdBusiness}
              label="Company"
              value={data.company?.companyName}
            />
          </Card>
        </div>
      </div>
    </Modal>
  );
};

export const AircraftCharterWantedForms = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.aircraftCharterWanted.getAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "aircraft",
        title: "Aircraft",
        accessor: (r: any) =>
          r.anyAircraftType ? "Any" : r.aircraftType?.name,
      },
      {
        key: "engine",
        title: "Engine",
        accessor: (r: any) => (r.anyEngineType ? "Any" : r.engineType?.code),
      },
      {
        key: "company",
        title: "Company",
        accessor: (r: any) => r.company?.companyName,
      },
      {
        key: "confirmStatus",
        title: "Status",
        type: "number",
        accessor: (r: any) => r.confirmStatus,
        render: (_: any, v: any) => (
          <Badge
            label={(ConfirmStatusText as any)[v]}
            color={(ConfirmStatusColor as any)[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        filterable: false,
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailId(r.id),
              },
              {
                key: "confirm",
                icon: MdCheck,
                color: "#F59E0B",
                onClick: () => setStatusRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Aircraft Charter Wanted
        </div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          Manage charter wanted aircraft
        </div>
      </div>

      <Table loading={loading} columns={columns} data={data} />

      {detailId && (
        <AircraftCharterWantedDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {statusRow && (
        <ConfirmStatusModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
